// Admin Contract Create - Crear nuevo contrato (hiring code)
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/providers/AuthProvider';
import { adminService } from '@/services/adminService';
import { crmService } from '@/services/crmService';
import { contractsService } from '@/services/contractsService';
import { CONTRACT_TYPE_LABELS, CONTRACT_TYPE_PRICING, GRADE_DESCRIPTIONS, type ClientGrade, type ContractType, type PaymentType } from '@/types/admin';
import type { Contact } from '@/types/crm';
import type { ContractTitular, ContractDependent, ExtractedHiringCodeItem, AdminContractCreateResponse, CustomPaymentMethod } from '@/types/contracts';
import { CUSTOM_PAYMENT_METHOD_LABELS } from '@/types/contracts';
import { extractHiringCodes } from '@/utils/extractHiringCodes';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import {
  ArrowLeftIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  UserPlusIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

interface AdminContractCreateProps {
  /** Ruta base para listado y navegación (ej. /crm/contratos-new) */
  contractsListPath?: string;
}

export function AdminContractCreate({ contractsListPath = '/crm/contracts' }: AdminContractCreateProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated: isAuthFromProvider, isLoading: isAuthLoading } = useAuth();
  const listPath = contractsListPath;
  /** En contexto CRM (/crm/...) permitimos agent y lawyer; en admin solo admin/superuser */
  const isCrmContext = listPath.startsWith('/crm/');
  const canAccess = isCrmContext ? isAuthFromProvider : adminService.isAuthenticated();
  const getDetailPath = (code: string) => `${listPath.replace(/\/$/, '')}/${code}`;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  /** Códigos creados normalizados para UI: 1 = individual, 2 = oferta 2x1 (linked). */
  const [generatedCodes, setGeneratedCodes] = useState<ExtractedHiringCodeItem[] | null>(null);

  // Form fields
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPassport, setUserPassport] = useState('');
  const [userNie, setUserNie] = useState('');
  const [userNationality, setUserNationality] = useState('');
  const [userAddress, setUserAddress] = useState('');
  const [userCity, setUserCity] = useState('');
  const [userProvince, setUserProvince] = useState('');
  const [userPostalCode, setUserPostalCode] = useState('');
  const [serviceName, setServiceName] = useState('Residencia Legal en España');
  const [serviceDescription, setServiceDescription] = useState('Tramitación de expediente para obtención de residencia legal');
  const [contractType, setContractType] = useState<ContractType>('individual');
  const [grade, setGrade] = useState<ClientGrade>('B');
  const [paymentType, setPaymentType] = useState<PaymentType>('one_time');
  /** Importe en EUR para modalidad "Pago único 100%" (precio variable). Default = precio estándar del tipo elegido. */
  const [oneTimeFullAmount, setOneTimeFullAmount] = useState<number>(CONTRACT_TYPE_PRICING['individual'].one_time);
  const [manualPaymentMode, setManualPaymentMode] = useState(false);
  const [manualPaymentNote, setManualPaymentNote] = useState('');

  // Segunda persona (solo para contractType === 'linked' - Oferta 2x1)
  const [linkedName2, setLinkedName2] = useState('');
  const [linkedEmail2, setLinkedEmail2] = useState('');
  const [linkedPassport2, setLinkedPassport2] = useState('');
  const [linkedNie2, setLinkedNie2] = useState('');
  const [linkedPaymentType2, setLinkedPaymentType2] = useState<PaymentType>('one_time');

  // Anexos al contrato (sin hiring_code hasta que se cree el contrato)
  const [annexes, setAnnexes] = useState<Array<{ title: string; content: string }>>([]);
  const [showAnnexModal, setShowAnnexModal] = useState(false);
  const [editingAnnexIndex, setEditingAnnexIndex] = useState<number | null>(null);
  const [annexForm, setAnnexForm] = useState({ title: '', content: '' });

  // Titular 1 = siempre "Datos del Cliente" (arriba). Titular 2 = opcional "firmante suplementario". Titular = firmante (todos firman). Menores = dependientes (no firman).
  const [supplementaryHolder, setSupplementaryHolder] = useState<Partial<ContractTitular> | null>(null);
  const [dependents, setDependents] = useState<ContractDependent[]>([]);
  const [showMultisignSection, setShowMultisignSection] = useState(false);

  // Modo contrato personalizado
  const [isCustomContract, setIsCustomContract] = useState(false);
  const [customAmount, setCustomAmount] = useState<number>(0);
  const [customInstallments, setCustomInstallments] = useState<number>(1);
  const [customPaymentMethod, setCustomPaymentMethod] = useState<CustomPaymentMethod>('stripe');

  // Idioma del contrato (flujo público y PDF)
  const [language, setLanguage] = useState<'es' | 'en' | 'fr'>('es');

  // Contact search
  const [contactSearch, setContactSearch] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState<Contact[]>([]);
  const [contactSearchLoading, setContactSearchLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const contactSearchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contactSearchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // No redirigir mientras el auth sigue cargando (evita flash para agent/lawyer en CRM)
    if (isCrmContext && isAuthLoading) return;
    // CRM: cualquier usuario autenticado (agent, lawyer, admin). Admin panel: solo admin/superuser
    if (!canAccess) {
      navigate('/auth/login');
    }
  }, [navigate, canAccess, isCrmContext, isAuthLoading]);

  // Duo: abrir sección y mostrar bloque del segundo adulto obligatorio. Family/Individual: abrir sección (2º adulto opcional). Linked: cerrar sección (tiene su propio formulario Persona B).
  useEffect(() => {
    if (contractType === 'duo') {
      setShowMultisignSection(true);
      setSupplementaryHolder(prev => prev === null ? { full_name: '', email: '', is_primary: false } : prev);
    } else if (contractType === 'family' || contractType === 'individual') {
      setShowMultisignSection(true);
    } else if (contractType === 'linked') {
      // Para linked el segundo participante tiene su propio formulario (Datos Persona B); cerrar sección multifirma para no confundir.
      setShowMultisignSection(false);
    }
  }, [contractType]);

  // Sincronizar el importe por defecto de "Pago único 100%" con el tipo de contrato y grado.
  // Solo actualiza cuando cambia el tipo o grado (no pisa ediciones manuales del usuario dentro de una misma selección).
  useEffect(() => {
    const pricing = grade === 'T'
      ? { one_time: 1 }
      : CONTRACT_TYPE_PRICING[contractType];
    setOneTimeFullAmount(pricing.one_time);
  }, [contractType, grade]);

  // Buscar contactos con debounce
  useEffect(() => {
    if (contactSearchTimeoutRef.current) {
      clearTimeout(contactSearchTimeoutRef.current);
    }

    if (contactSearch.trim().length < 2) {
      setContactSearchResults([]);
      setShowContactDropdown(false);
      return;
    }

    contactSearchTimeoutRef.current = setTimeout(async () => {
      setContactSearchLoading(true);
      try {
        const response = await crmService.getContacts({
          search: contactSearch.trim(),
          limit: 10,
          skip: 0,
        });
        setContactSearchResults(response.items || []);
        setShowContactDropdown(true);
      } catch (error) {
        console.error('Error buscando contactos:', error);
        setContactSearchResults([]);
      } finally {
        setContactSearchLoading(false);
      }
    }, 300);

    return () => {
      if (contactSearchTimeoutRef.current) {
        clearTimeout(contactSearchTimeoutRef.current);
      }
    };
  }, [contactSearch]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contactSearchRef.current && !contactSearchRef.current.contains(event.target as Node)) {
        setShowContactDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectContact = (contact: Contact) => {
    setSelectedContact(contact);
    setContactSearch(contact.name || `${contact.first_name} ${contact.last_name || ''}`.trim());
    setShowContactDropdown(false);
    
    // Precargar datos del contacto
    if (contact.name || contact.first_name) {
      setUserName(contact.name || `${contact.first_name} ${contact.last_name || ''}`.trim());
    }
    if (contact.email) {
      setUserEmail(contact.email);
    }
    if (contact.nacionalidad) {
      setUserNationality(contact.nacionalidad);
    }
    if (contact.address) {
      setUserAddress(contact.address);
    }
    if (contact.city) {
      setUserCity(contact.city);
    }
    if (contact.state) {
      setUserProvince(contact.state);
    }
    if (contact.postal_code) {
      setUserPostalCode(contact.postal_code);
    }
    // Nota: Pasaporte y NIE no están en Contact por defecto, pero si están en custom_fields, se pueden obtener
    // Por ahora, dejamos esos campos vacíos ya que no son campos estándar del contacto
  };

  const handleClearContact = () => {
    setSelectedContact(null);
    setContactSearch('');
    setContactSearchResults([]);
    setShowContactDropdown(false);
  };

  // Pre-llenar desde un contacto pasado vía navigate(..., { state: { contact } }).
  // Usado por el botón "Nuevo contrato" en CRMContactDetail. Se ejecuta una sola vez
  // al montar; si no hay contact en state, no hace nada.
  const prefilledFromStateRef = useRef(false);
  useEffect(() => {
    if (prefilledFromStateRef.current) return;
    const stateContact = (location.state as { contact?: Contact } | null)?.contact;
    if (stateContact) {
      handleSelectContact(stateContact);
      prefilledFromStateRef.current = true;
    }
  }, [location.state]);

  // Funciones para gestionar anexos
  const handleAddAnnex = () => {
    setAnnexForm({ title: '', content: '' });
    setEditingAnnexIndex(null);
    setShowAnnexModal(true);
  };

  const handleEditAnnex = (index: number) => {
    setAnnexForm(annexes[index]);
    setEditingAnnexIndex(index);
    setShowAnnexModal(true);
  };

  const handleDeleteAnnex = (index: number) => {
    if (confirm('¿Estás seguro de que deseas eliminar este anexo?')) {
      setAnnexes(annexes.filter((_, i) => i !== index));
    }
  };

  const handleSaveAnnex = () => {
    if (!annexForm.title.trim() || !annexForm.content.trim()) {
      alert('Por favor completa todos los campos del anexo');
      return;
    }

    if (editingAnnexIndex !== null) {
      // Editar anexo existente
      const updated = [...annexes];
      updated[editingAnnexIndex] = { ...annexForm };
      setAnnexes(updated);
    } else {
      // Agregar nuevo anexo
      setAnnexes([...annexes, { ...annexForm }]);
    }

    setShowAnnexModal(false);
    setAnnexForm({ title: '', content: '' });
    setEditingAnnexIndex(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validations
    if (!userName.trim()) {
      setError('El nombre completo es obligatorio');
      return;
    }

    if (!userEmail.trim() || !userEmail.includes('@')) {
      setError('El email es obligatorio y debe ser válido');
      return;
    }

    if (!userPassport.trim() && !userNie.trim()) {
      setError('Debe proporcionar al menos Pasaporte o NIE');
      return;
    }

    // Validaciones contrato personalizado
    if (isCustomContract) {
      if (!customAmount || customAmount <= 0) {
        setError('El precio del contrato personalizado debe ser mayor que 0');
        return;
      }
      if (!customInstallments || customInstallments < 1 || customInstallments > 99) {
        setError('El número de cuotas debe estar entre 1 y 99');
        return;
      }
    }

    if (manualPaymentMode && !manualPaymentNote.trim()) {
      setError('Debes describir cómo se realizó el pago previo');
      return;
    }

    if (paymentType === 'one_time_full' && (oneTimeFullAmount <= 0 || !Number.isFinite(oneTimeFullAmount))) {
      setError('El importe para Pago único (100%) debe ser mayor que 0');
      return;
    }

    if (contractType === 'linked') {
      if (!linkedName2.trim()) {
        setError('El nombre de la Persona B (Oferta 2x1) es obligatorio');
        return;
      }
      if (!linkedEmail2.trim() || !linkedEmail2.includes('@')) {
        setError('El email de la Persona B es obligatorio y debe ser válido');
        return;
      }
      if (!linkedPassport2.trim() && !linkedNie2.trim()) {
        setError('Persona B: debe proporcionar al menos Pasaporte o NIE');
        return;
      }
    }

    const suppName = supplementaryHolder && String(supplementaryHolder.full_name ?? '').trim();
    const suppEmail = supplementaryHolder && String(supplementaryHolder.email ?? '').trim();
    const hasSupplementary = !!(suppName && suppEmail);
    const missingSuppFields: string[] = [];
    // Solo Pareja/Duo exige segundo adulto; Unidad familiar puede ser 1 adulto + hijos.
    if (contractType === 'duo') {
      if (!supplementaryHolder) {
        setError('Para contrato Pareja/Duo debe rellenar los datos del segundo adulto (firmante suplementario) en la sección correspondiente.');
        return;
      }
      if (!suppName) missingSuppFields.push('Nombre completo');
      if (!suppEmail) missingSuppFields.push('Correo electrónico');
    }
    if (contractType === 'duo' && !hasSupplementary) {
      setError(
        missingSuppFields.length > 0
          ? `Firmante suplementario (segundo adulto): faltan campos obligatorios: ${missingSuppFields.join(' y ')}. Rellénelos en la sección "Firmante suplementario (segundo adulto)".`
          : 'Para contrato Pareja/Duo debe rellenar los datos del segundo adulto (firmante suplementario) en la sección correspondiente.'
      );
      return;
    }

    if (contractType === 'family') {
      // Family: segundo adulto es opcional (padre/madre soltero/a con hijos)
      if (supplementaryHolder) {
        if (!suppName) missingSuppFields.push('Nombre completo');
        if (!suppEmail) missingSuppFields.push('Correo electrónico');
        if (missingSuppFields.length > 0) {
          setError(`Firmante suplementario (segundo adulto): faltan campos obligatorios: ${missingSuppFields.join(' y ')}.`);
          return;
        }
      }
      const validDependents = dependents.filter(dep => dep.full_name.trim());
      if (validDependents.length === 0) {
        setError('Para Unidad familiar debe añadir al menos 1 menor/dependiente (hijo/a).');
        return;
      }
      if (validDependents.length > 3) {
        setError('Máximo 3 menores/dependientes en Unidad familiar');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setGeneratedCodes(null);

    try {
      // Titular 1 = Datos del Cliente (siempre). Titular 2 = firmante suplementario si está rellenado.
      const primaryDocId = userNie || userPassport || undefined;
      const primaryDocType = userNie ? ('nie' as const) : (userPassport ? ('passport' as const) : undefined);
      const titularsPayload: ContractTitular[] = [
        { full_name: userName, email: userEmail, document_id: primaryDocId, document_type: primaryDocType, address: userAddress || undefined, city: userCity || undefined, province: userProvince || undefined, postal_code: userPostalCode || undefined, nationality: userNationality || undefined, is_primary: true },
        ...(supplementaryHolder && String(supplementaryHolder.full_name ?? '').trim() && String(supplementaryHolder.email ?? '').trim()
          ? [{ full_name: String(supplementaryHolder.full_name).trim(), email: String(supplementaryHolder.email).trim(), phone: supplementaryHolder.phone, document_id: supplementaryHolder.document_id, document_type: supplementaryHolder.document_type, address: supplementaryHolder.address, city: supplementaryHolder.city, province: supplementaryHolder.province, postal_code: supplementaryHolder.postal_code, nationality: supplementaryHolder.nationality, is_primary: false }]
          : []),
      ];

      // Calcular amount y campos según modo
      let totalAmount: number;
      let effectiveContractType: ContractType;
      let effectivePaymentType: PaymentType;
      let isManualPayment = manualPaymentMode;
      let manualNote = manualPaymentNote;
      let manualMethod = manualPaymentMode ? `Pago previo registrado: ${manualPaymentNote}` : undefined;

      if (isCustomContract) {
        totalAmount = customAmount;
        // Inferir contract_type: 2 titulares=duo, con dependientes=family, else individual
        const hasDeps = dependents.filter(dep => dep.full_name.trim()).length > 0;
        const hasSecond = !!(supplementaryHolder && String(supplementaryHolder.full_name ?? '').trim());
        effectiveContractType = hasDeps ? 'family' : hasSecond ? 'duo' : 'individual';
        // Inferir payment_type para compatibilidad backend
        effectivePaymentType = customInstallments === 1 ? 'one_time_full' : customInstallments === 2 ? 'one_time' : 'subscription';
        // Si método no es stripe, marcar pago manual
        if (customPaymentMethod !== 'stripe') {
          isManualPayment = true;
          manualNote = manualNote || `Contrato personalizado - ${CUSTOM_PAYMENT_METHOD_LABELS[customPaymentMethod]}`;
          manualMethod = CUSTOM_PAYMENT_METHOD_LABELS[customPaymentMethod];
        }
      } else {
        const pricing = getEffectivePricing();
        totalAmount =
          paymentType === 'one_time_full'
            ? oneTimeFullAmount
            : paymentType === 'subscription'
              ? pricing.subscription
              : pricing.one_time;
        effectiveContractType = contractType;
        effectivePaymentType = paymentType;
      }

      const requestBody = {
        grade,
        payment_type: effectivePaymentType,
        contract_type: effectiveContractType,
        contract_template: 'standard',
        expires_in_days: 30,
        amount: totalAmount,
        currency: 'EUR',
        client_name: userName,
        client_email: userEmail,
        client_nationality: userNationality || undefined,
        client_passport: userPassport || undefined,
        client_nie: userNie || undefined,
        client_address: userAddress || undefined,
        client_city: userCity || undefined,
        client_province: userProvince || undefined,
        client_postal_code: userPostalCode || undefined,
        service_name: serviceName,
        service_description: serviceDescription,
        manual_payment_confirmed: isManualPayment,
        manual_payment_note: isManualPayment ? manualNote : undefined,
        manual_payment_method: isManualPayment ? manualMethod : undefined,
        ...(titularsPayload.length > 0 && { titulars: titularsPayload }),
        ...((() => { const d = dependents.filter(dep => dep.full_name.trim()); return d.length > 0 ? { dependents: d } : {}; })()),
        ...(!isCustomContract && contractType === 'linked' && {
          linked_second_participant: {
            client_name: linkedName2.trim(),
            client_email: linkedEmail2.trim(),
            client_passport: linkedPassport2.trim() || undefined,
            client_nie: linkedNie2.trim() || undefined,
            payment_type: linkedPaymentType2,
          },
        }),
        // Campos contrato personalizado
        ...(isCustomContract && {
          is_custom: true,
          num_installments: customInstallments,
          custom_payment_method: customPaymentMethod,
        }),
        // Idioma del contrato
        language,
      };


      console.log('📤 REQUEST POST /crm/contracts/:', JSON.stringify(requestBody, null, 2));

      const created: AdminContractCreateResponse = await contractsService.createContractV2(requestBody);
      const codesToShow = extractHiringCodes(created);

      if (!codesToShow.length) {
        console.warn('⚠️ No se encontró código de contratación en la respuesta', created);
        setError('Código creado pero no se pudo extraer de la respuesta');
        return;
      }

      setGeneratedCodes(codesToShow);
      setSuccess(codesToShow.length === 2
        ? '¡Oferta 2x1 creada! Dos códigos de contratación listos.'
        : '¡Código de contratación creado exitosamente!');

      const firstCode = codesToShow[0].hiringCode;
      const firstShortUrl = codesToShow[0].shortUrl || `${window.location.origin}/contratacion/${firstCode}`;
      if (!codesToShow[0].shortUrl && firstCode) {
        console.log('🔗 URL generada manualmente:', firstShortUrl);
      }

      // Guardar anexos al primer contrato (en 2x1 solo al primero)
      if (annexes.length > 0 && firstCode) {
        console.log('📎 Guardando anexos al contrato...', annexes.length);
        try {
          for (const annex of annexes) {
            await contractsService.createAnnex({
              hiring_code: firstCode,
              title: annex.title,
              content: annex.content,
            });
          }
          console.log('✅ Anexos guardados exitosamente');
        } catch (annexError: any) {
          console.error('⚠️ Error guardando anexos:', annexError);
          // No bloqueamos el éxito del contrato si falla guardar anexos
          // El usuario puede agregarlos manualmente después
        }
      }
      
      // Clear form
      setUserName('');
      setUserEmail('');
      setUserPassport('');
      setUserNie('');
      setUserNationality('');
      setUserAddress('');
      setUserCity('');
      setUserProvince('');
      setUserPostalCode('');
      setGrade('B');
      setPaymentType('one_time');
      setOneTimeFullAmount(CONTRACT_TYPE_PRICING[contractType].one_time);
      setManualPaymentMode(false);
      setManualPaymentNote('');
      setAnnexes([]);
      setSupplementaryHolder(null);
      setDependents([]);
      setShowMultisignSection(false);
      setIsCustomContract(false);
      setCustomAmount(0);
      setCustomInstallments(1);
      setCustomPaymentMethod('stripe');
      handleClearContact();
    } catch (err: any) {
      console.error('Error creando contrato:', err);
      setError(err.message || 'Error al crear el código de contratación');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('¡Copiado al portapapeles!');
  };

  /** Precio efectivo según contractType (y grade T para testing). Para linked es el precio por contrato. */
  const getEffectivePricing = () => {
    const pricing = grade === 'T'
      ? { one_time: 1, subscription: 1, monthly: 0.1 }
      : CONTRACT_TYPE_PRICING[contractType];
    return pricing;
  };

  const getPaymentInfo = () => {
    const pricing = getEffectivePricing();
    const totalAmount =
      paymentType === 'one_time_full'
        ? oneTimeFullAmount
        : paymentType === 'subscription'
          ? pricing.subscription
          : pricing.one_time;

    let firstPayment: number;
    let secondPayment: number;
    let monthlyPayment: number;
    let numberOfPayments: number;

    if (paymentType === 'one_time_full') {
      numberOfPayments = 1;
      monthlyPayment = 0;
      firstPayment = totalAmount;
      secondPayment = 0;
    } else if (paymentType === 'subscription') {
      numberOfPayments = 10;
      monthlyPayment = pricing.monthly;
      firstPayment = monthlyPayment;
      secondPayment = 0;
    } else {
      numberOfPayments = 2;
      firstPayment = totalAmount / 2;
      secondPayment = totalAmount / 2;
      monthlyPayment = 0;
    }

    return {
      total: totalAmount,
      first: firstPayment,
      second: secondPayment,
      monthly: monthlyPayment,
      numberOfPayments,
    };
  };

  const paymentInfo = getPaymentInfo();

  const handleViewContract = () => {
    const firstCode = generatedCodes?.[0]?.hiringCode;
    if (firstCode) {
      navigate(getDetailPath(firstCode));
    }
  };

  if (loading && !generatedCodes?.length) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" text="Creando contrato..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Crear Nuevo Contrato</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Genera un nuevo código de contratación con todos los datos necesarios
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(listPath)}
          className="flex items-center gap-2"
        >
          <ArrowLeftIcon width={18} height={18} />
          Volver
        </Button>
      </div>

      {/* Success Message */}
      {success && generatedCodes?.length && (
        <Card className="border-green-500 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="text-green-600 mt-1 shrink-0" width={24} height={24} />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-green-900 mb-2">{success}</h3>
                <div className="bg-white border border-green-300 rounded-lg p-4 space-y-4">
                  {generatedCodes.map((item, index) => (
                    <div key={item.hiringCode} className="space-y-3">
                      {generatedCodes.length > 1 && (
                        <p className="text-sm font-medium text-gray-700">
                          {item.clientName ? `Contrato ${index + 1}: ${item.clientName}` : `Código ${index + 1}`}
                        </p>
                      )}
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Código de contratación:</p>
                        <div className="flex items-center gap-2">
                          <code className="text-xl sm:text-2xl font-bold text-primary break-all">{item.hiringCode}</code>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(item.hiringCode)}
                            aria-label="Copiar código"
                          >
                            <DocumentDuplicateIcon width={16} height={16} />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">URL de contratación:</p>
                        <div className="flex items-center gap-2">
                          <code className="text-sm bg-gray-100 px-3 py-2 rounded flex-1 break-all">
                            {item.shortUrl || `${window.location.origin}/contratacion/${item.hiringCode}`}
                          </code>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(item.shortUrl || `${window.location.origin}/contratacion/${item.hiringCode}`)}
                            aria-label="Copiar URL"
                          >
                            <DocumentDuplicateIcon width={16} height={16} />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">URL solo mandato:</p>
                        <div className="flex items-center gap-2">
                          <code className="text-sm bg-gray-100 px-3 py-2 rounded flex-1 break-all">
                            {`${window.location.origin}/mandato/${item.hiringCode}`}
                          </code>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(`${window.location.origin}/mandato/${item.hiringCode}`)}
                            aria-label="Copiar URL mandato"
                          >
                            <DocumentDuplicateIcon width={16} height={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-3 border-t">
                    <Button onClick={handleViewContract} className="flex-1">
                      Ver primer contrato
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setGeneratedCodes(null);
                        setSuccess(null);
                      }}
                      className="flex-1"
                    >
                      Crear otro
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form */}
      {!generatedCodes?.length && (
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <UserPlusIcon className="text-primary" width={24} height={24} />
              Datos del Contrato
            </CardTitle>
          </CardHeader>

          <CardContent>
            <form data-testid="contract-create-form" onSubmit={handleSubmit} className="space-y-6">
              {/* Selector: Contrato habitual vs Personalizado */}
              <div>
                <h3 className="font-semibold text-lg mb-3 text-gray-900">Modo de contrato</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setIsCustomContract(false)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      !isCustomContract
                        ? 'border-primary bg-primary/10 shadow-md'
                        : 'border-gray-300 bg-white hover:border-primary'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">Contrato habitual</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Precios fijos por tipo (individual, duo, familia, 2x1) con modalidades estándar
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCustomContract(true)}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      isCustomContract
                        ? 'border-purple-600 bg-purple-50 shadow-md'
                        : 'border-gray-300 bg-white hover:border-purple-400'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">Contrato personalizado</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Precio libre, cuotas personalizadas y método de pago flexible
                    </div>
                  </button>
                </div>
              </div>

              {/* Idioma del contrato */}
              <div>
                <h3 className="font-semibold text-lg mb-3 text-gray-900">Idioma del contrato</h3>
                <div className="grid grid-cols-3 gap-3">
                  {([
                    { value: 'es' as const, label: 'Español', flag: '🇪🇸' },
                    { value: 'en' as const, label: 'English', flag: '🇬🇧' },
                    { value: 'fr' as const, label: 'Français', flag: '🇫🇷' },
                  ]).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setLanguage(opt.value)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        language === opt.value
                          ? 'border-primary bg-primary/10 shadow-md'
                          : 'border-gray-300 bg-white hover:border-primary'
                      }`}
                    >
                      <div className="text-xl">{opt.flag}</div>
                      <div className="font-semibold text-gray-900 text-sm mt-1">{opt.label}</div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  El cliente verá el flujo de contratación y el PDF del contrato en este idioma
                </p>
              </div>

              {/* Buscar Contacto Existente */}
              <div>
                <Label htmlFor="contact-search" className="text-base font-semibold text-gray-900 mb-2 block">
                  Buscar Contacto Existente (Opcional)
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Busca un contacto existente para precargar sus datos en el formulario
                </p>
                <div className="relative" ref={contactSearchRef}>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="contact-search"
                      type="text"
                      placeholder="Buscar por nombre, email o teléfono..."
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      onFocus={() => {
                        if (contactSearchResults.length > 0) {
                          setShowContactDropdown(true);
                        }
                      }}
                      className="pl-10 pr-10"
                    />
                    {selectedContact && (
                      <button
                        type="button"
                        onClick={handleClearContact}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Dropdown de resultados */}
                  {showContactDropdown && (contactSearchResults.length > 0 || contactSearchLoading) && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {contactSearchLoading ? (
                        <div className="p-4 text-center text-sm text-gray-500">
                          Buscando contactos...
                        </div>
                      ) : (
                        <>
                          {contactSearchResults.map((contact) => (
                            <button
                              key={contact.id}
                              type="button"
                              onClick={() => handleSelectContact(contact)}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                              onMouseDown={(e) => e.preventDefault()}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-900 truncate">
                                    {contact.name || `${contact.first_name} ${contact.last_name || ''}`.trim() || 'Sin nombre'}
                                  </div>
                                  {contact.email && (
                                    <div className="text-sm text-gray-500 truncate">{contact.email}</div>
                                  )}
                                  {contact.phone && (
                                    <div className="text-sm text-gray-500 truncate">{contact.phone}</div>
                                  )}
                                </div>
                                <UsersIcon className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" />
                              </div>
                            </button>
                          ))}
                          {contactSearchResults.length === 0 && contactSearch.trim().length >= 2 && (
                            <div className="p-4 text-center text-sm text-gray-500">
                              No se encontraron contactos
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                {selectedContact && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-green-900">
                          Contacto seleccionado: {selectedContact.name || `${selectedContact.first_name} ${selectedContact.last_name || ''}`.trim()}
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          Los datos se han precargado en el formulario. Puedes editarlos si es necesario.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleClearContact}
                        className="text-green-700 hover:text-green-900"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Datos del Cliente */}
              <div>
                <h3 className="font-semibold text-lg mb-4 text-gray-900">Datos del Cliente</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="userName">Nombre y Apellidos *</Label>
                    <Input
                      id="userName"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="Juan Pérez García"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="userEmail">Correo Electrónico *</Label>
                    <Input
                      id="userEmail"
                      type="email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      placeholder="cliente@ejemplo.com"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="userPassport">Número de Pasaporte</Label>
                    <Input
                      id="userPassport"
                      value={userPassport}
                      onChange={(e) => setUserPassport(e.target.value.toUpperCase())}
                      placeholder="ABC123456"
                    />
                  </div>

                  <div>
                    <Label htmlFor="userNie">NIE</Label>
                    <Input
                      id="userNie"
                      value={userNie}
                      onChange={(e) => setUserNie(e.target.value.toUpperCase())}
                      placeholder="X1234567Y"
                    />
                  </div>

                  <div>
                    <Label htmlFor="userNationality">Nacionalidad</Label>
                    <Input
                      id="userNationality"
                      value={userNationality}
                      onChange={(e) => setUserNationality(e.target.value)}
                      placeholder="Ej: Venezolana, Colombiana, Española..."
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <Label htmlFor="userAddress">Dirección</Label>
                    <Input
                      id="userAddress"
                      value={userAddress}
                      onChange={(e) => setUserAddress(e.target.value)}
                      placeholder="Calle Principal 123"
                    />
                  </div>

                  <div>
                    <Label htmlFor="userCity">Ciudad</Label>
                    <Input
                      id="userCity"
                      value={userCity}
                      onChange={(e) => setUserCity(e.target.value)}
                      placeholder="Salamanca"
                    />
                  </div>
                </div>

                {/* Provincia y Código Postal */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label htmlFor="userProvince">Provincia (opcional)</Label>
                    <Input
                      id="userProvince"
                      value={userProvince}
                      onChange={(e) => setUserProvince(e.target.value)}
                      placeholder="Salamanca"
                    />
                  </div>

                  <div>
                    <Label htmlFor="userPostalCode">Código Postal (opcional)</Label>
                    <Input
                      id="userPostalCode"
                      value={userPostalCode}
                      onChange={(e) => setUserPostalCode(e.target.value)}
                      placeholder="37001"
                    />
                  </div>
                </div>
              </div>

              {/* Segundo adulto (firmante suplementario) y menores. Titular = firmante; solo los menores no firman. Individual: permite madre/padre soltero con hijos. */}
              <div className="border border-gray-200 rounded-lg p-4">
                <button
                  type="button"
                  onClick={() => setShowMultisignSection(!showMultisignSection)}
                  className="flex items-center justify-between w-full text-left font-semibold text-gray-900"
                >
                    <span>
                    {contractType === 'individual'
                      ? 'Menores a cargo y segundo adulto (opcional)'
                      : contractType === 'family'
                        ? 'Menores a cargo y segundo adulto (opcional)'
                        : 'Segundo adulto y menores'}
                    {contractType === 'duo' && (
                      <span className="text-amber-600 font-normal ml-2">(requerido: 2º adulto)</span>
                    )}
                    {contractType === 'family' && (
                      <span className="text-amber-600 font-normal ml-2">(requerido: 1-3 hijos · 2º adulto opcional)</span>
                    )}
                  </span>
                  <span className="text-gray-500">{showMultisignSection ? '▼' : '▶'}</span>
                </button>
                {showMultisignSection && (
                  <div className="mt-4 space-y-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      {contractType === 'individual'
                        ? 'Puede añadir hijos o menores a cargo (madre o padre soltero). Opcionalmente, un segundo adulto firmante. Los titulares firman; los menores no firman.'
                        : contractType === 'family'
                          ? 'Un titular con al menos un hijo/a (1-3). Opcionalmente puede añadir un segundo adulto firmante. Los titulares firman; los menores no firman.'
                          : 'El cliente principal (arriba, en Datos del Cliente) es el primer titular y firmante. Para contrato Pareja/Duo añada los datos del segundo adulto (firmante suplementario). Los titulares firman; los menores no firman.'}
                    </p>
                    {/* Firmante suplementario (segundo adulto) — único bloque con "Quitar" */}
                    <div>
                      <Label className="mb-2 block">Firmante suplementario (segundo adulto)</Label>
                      {(contractType === 'duo' || contractType === 'family' || supplementaryHolder !== null) ? (
                        <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex flex-wrap gap-2 items-end">
                            <Input
                              placeholder={contractType === 'duo' ? 'Nombre completo *' : 'Nombre completo'}
                              value={supplementaryHolder?.full_name ?? ''}
                              onChange={e => setSupplementaryHolder(prev => ({ ...(prev ?? {}), full_name: e.target.value }))}
                              className="flex-1 min-w-[140px]"
                            />
                            <Input
                              type="email"
                              placeholder={contractType === 'duo' ? 'Email *' : 'Email'}
                              value={supplementaryHolder?.email ?? ''}
                              onChange={e => setSupplementaryHolder(prev => ({ ...(prev ?? {}), email: e.target.value.trim() }))}
                              className="flex-1 min-w-[140px]"
                            />
                            {/* Para Duo el segundo adulto es obligatorio; ocultamos el botón "Quitar" para evitar estado inválido */}
                            {contractType !== 'duo' && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setSupplementaryHolder(null)}
                                aria-label="Quitar firmante suplementario"
                              >
                                Quitar
                              </Button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 items-end">
                            <Input
                              placeholder="Teléfono (opc.)"
                              value={supplementaryHolder?.phone ?? ''}
                              onChange={e => setSupplementaryHolder(prev => ({ ...(prev ?? {}), phone: e.target.value }))}
                              className="min-w-[120px]"
                            />
                            <select
                              value={supplementaryHolder?.document_type ?? ''}
                              onChange={e => setSupplementaryHolder(prev => ({ ...(prev ?? {}), document_type: (e.target.value || undefined) as 'nif' | 'nie' | 'passport' | undefined }))}
                              className="min-w-[120px] h-9 px-3 rounded-md border border-input bg-background text-sm"
                            >
                              <option value="">Tipo doc.</option>
                              <option value="nif">NIF</option>
                              <option value="nie">NIE</option>
                              <option value="passport">Pasaporte</option>
                            </select>
                            <Input
                              placeholder={supplementaryHolder?.document_type === 'nie' || supplementaryHolder?.document_type === 'nif' ? 'Nº NIE/NIF (opc.)' : supplementaryHolder?.document_type === 'passport' ? 'Nº Pasaporte (opc.)' : 'Nº documento (opc.)'}
                              value={supplementaryHolder?.document_id ?? ''}
                              onChange={e => setSupplementaryHolder(prev => ({ ...(prev ?? {}), document_id: e.target.value }))}
                              className="min-w-[140px]"
                            />
                            <Input
                              placeholder="Nacionalidad (opc.)"
                              value={supplementaryHolder?.nationality ?? ''}
                              onChange={e => setSupplementaryHolder(prev => ({ ...(prev ?? {}), nationality: e.target.value }))}
                              className="min-w-[140px]"
                            />
                          </div>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setSupplementaryHolder({ full_name: '', email: '', is_primary: false })}
                        >
                          + Añadir firmante suplementario
                        </Button>
                      )}
                    </div>
                    {/* Menores / dependientes (no firman) */}
                    <div>
                      <Label className="mb-2 block">Menores / dependientes (no firman)</Label>
                      <p className="text-xs text-gray-500 mb-2">Hijos/as u otros menores incluidos en el expediente. No firman el contrato.</p>
                      {dependents.map((d, i) => (
                        <div key={i} className="space-y-2 mb-2 p-2 bg-gray-50 rounded">
                          <div className="flex flex-wrap items-end gap-2">
                            <Input placeholder="Nombre completo" value={d.full_name} onChange={e => setDependents(prev => { const n = [...prev]; n[i] = { ...n[i], full_name: e.target.value }; return n; })} className="flex-1 min-w-[120px]" />
                            <Input type="date" placeholder="Fecha nac." value={d.date_of_birth} onChange={e => setDependents(prev => { const n = [...prev]; n[i] = { ...n[i], date_of_birth: e.target.value }; return n; })} />
                            <Input placeholder="Parentesco (ej. Hijo/a)" value={d.relationship} onChange={e => setDependents(prev => { const n = [...prev]; n[i] = { ...n[i], relationship: e.target.value }; return n; })} className="min-w-[100px]" />
                            <Button type="button" variant="outline" size="sm" onClick={() => setDependents(prev => prev.filter((_, j) => j !== i))}>Quitar</Button>
                          </div>
                          <div className="flex flex-wrap items-end gap-2">
                            <select
                              value={d.document_type ?? ''}
                              onChange={e => setDependents(prev => { const n = [...prev]; n[i] = { ...n[i], document_type: (e.target.value || undefined) as 'nif' | 'nie' | 'passport' | undefined }; return n; })}
                              className="min-w-[100px] h-9 px-3 rounded-md border border-input bg-background text-sm"
                            >
                              <option value="">Tipo doc.</option>
                              <option value="nif">NIF</option>
                              <option value="nie">NIE</option>
                              <option value="passport">Pasaporte</option>
                            </select>
                            <Input
                              placeholder={d.document_type === 'nie' || d.document_type === 'nif' ? 'Nº NIE/NIF (opc.)' : d.document_type === 'passport' ? 'Nº Pasaporte (opc.)' : 'Nº documento (opc.)'}
                              value={d.document_id ?? ''}
                              onChange={e => setDependents(prev => { const n = [...prev]; n[i] = { ...n[i], document_id: e.target.value || undefined }; return n; })}
                              className="min-w-[130px]"
                            />
                            <Input placeholder="Nacionalidad (opc.)" value={d.nationality ?? ''} onChange={e => setDependents(prev => { const n = [...prev]; n[i] = { ...n[i], nationality: e.target.value || undefined }; return n; })} className="min-w-[140px]" />
                          </div>
                        </div>
                      ))}
                      <Button type="button" data-testid="add-dependent-btn" variant="outline" size="sm" onClick={() => setDependents(prev => [...prev, { full_name: '', date_of_birth: '', relationship: '' }])}>+ Añadir menor/dependiente</Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Servicio */}
              <div>
                <h3 className="font-semibold text-lg mb-4 text-gray-900">Servicio</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="serviceName">Nombre del Servicio</Label>
                    <Input
                      id="serviceName"
                      value={serviceName}
                      onChange={(e) => setServiceName(e.target.value)}
                      placeholder="Residencia Legal en España"
                    />
                  </div>

                  <div>
                    <Label htmlFor="serviceDescription">Descripción</Label>
                    <Input
                      id="serviceDescription"
                      value={serviceDescription}
                      onChange={(e) => setServiceDescription(e.target.value)}
                      placeholder="Tramitación de expediente..."
                    />
                  </div>
                </div>
              </div>

              {/* === SECCIÓN PERSONALIZADA (solo si isCustomContract) === */}
              {isCustomContract && (
                <div className="border-2 border-purple-200 rounded-lg p-5 bg-purple-50/30 space-y-5">
                  <h3 className="font-semibold text-lg text-purple-900">Configuración personalizada</h3>

                  {/* Precio */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customAmount">Precio total (EUR) *</Label>
                      <Input
                        id="customAmount"
                        type="number"
                        min={1}
                        step={1}
                        value={customAmount || ''}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          setCustomAmount(Number.isNaN(v) ? 0 : Math.max(0, v));
                        }}
                        placeholder="Ej: 500"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="customInstallments">Número de cuotas (1-99) *</Label>
                      <Input
                        id="customInstallments"
                        type="number"
                        min={1}
                        max={99}
                        step={1}
                        value={customInstallments}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setCustomInstallments(Number.isNaN(v) ? 1 : Math.max(1, Math.min(99, v)));
                        }}
                        placeholder="1"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        1 = pago único, 2 = dos plazos (50%+50%), 3+ = cuotas mensuales iguales
                      </p>
                    </div>
                  </div>

                  {/* Método de pago */}
                  <div>
                    <Label className="mb-2 block">Método de pago</Label>
                    <div className="flex flex-wrap gap-3">
                      {(Object.keys(CUSTOM_PAYMENT_METHOD_LABELS) as CustomPaymentMethod[]).map((m) => (
                        <label key={m} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                          customPaymentMethod === m
                            ? 'border-purple-600 bg-purple-100'
                            : 'border-gray-300 bg-white hover:border-purple-300'
                        }`}>
                          <input
                            type="radio"
                            name="customPaymentMethod"
                            checked={customPaymentMethod === m}
                            onChange={() => setCustomPaymentMethod(m)}
                            className="accent-purple-600"
                          />
                          <span className="text-sm">{CUSTOM_PAYMENT_METHOD_LABELS[m]}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Grado (informativo) */}
                  <div>
                    <Label className="mb-2 block">Calificación (informativa)</Label>
                    <div className="flex gap-2">
                      {(['A', 'B', 'C', 'T'] as ClientGrade[]).map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setGrade(g)}
                          className={`px-4 py-2 rounded-lg border-2 transition-all font-bold ${
                            grade === g
                              ? (g === 'T' ? 'border-yellow-500 bg-yellow-500 text-white' : 'border-primary bg-primary text-white')
                              : 'border-gray-300 bg-white hover:border-primary'
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Solo informativa. El precio es el indicado arriba.</p>
                  </div>

                  {/* Resumen personalizado */}
                  {customAmount > 0 && (
                    <div className="bg-white border-2 border-purple-300 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Resumen</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total:</span>
                          <span className="font-bold text-lg text-purple-700">{customAmount}€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Cuotas:</span>
                          <span className="font-semibold">
                            {customInstallments === 1
                              ? 'Pago único'
                              : customInstallments === 2
                                ? `2 plazos de ${(customAmount / 2).toFixed(2)}€`
                                : `${customInstallments} cuotas de ${(customAmount / customInstallments).toFixed(2)}€`}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Método:</span>
                          <span className="font-semibold">{CUSTOM_PAYMENT_METHOD_LABELS[customPaymentMethod]}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Tipo de contrato + Calificación (solo habitual) */}
              {!isCustomContract && (<>
              <div>
                <h3 className="font-semibold text-lg mb-4 text-gray-900">
                  Tipo de contrato
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {(['individual', 'duo', 'family', 'linked'] as ContractType[]).map((ct) => (
                    <button
                      key={ct}
                      type="button"
                      data-testid={`contract-type-${ct}`}
                      onClick={() => setContractType(ct)}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        contractType === ct
                          ? 'border-primary bg-primary/10 shadow-md'
                          : 'border-gray-300 bg-white hover:border-primary'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">{CONTRACT_TYPE_LABELS[ct]}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {ct === 'individual' && '1 persona · 400€ / 480€'}
                        {ct === 'duo' && '2 familiares · 600€ / 680€'}
                        {ct === 'family' && '1-2 adultos + hijos · 600€ / 680€'}
                        {ct === 'linked' && '2 contratos 2x1 · 300€ c/u'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Datos Persona B (solo Oferta 2x1) */}
              {contractType === 'linked' && (
                <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/50">
                  <h3 className="font-semibold text-lg mb-4 text-gray-900">
                    Datos Persona B (Oferta 2x1)
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Se generarán dos contratos independientes con precio reducido. Cada persona tendrá su propio código de contratación.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label>Nombre y Apellidos *</Label>
                      <Input
                        value={linkedName2}
                        onChange={(e) => setLinkedName2(e.target.value)}
                        placeholder="Nombre completo persona B"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Correo Electrónico *</Label>
                      <Input
                        type="email"
                        value={linkedEmail2}
                        onChange={(e) => setLinkedEmail2(e.target.value)}
                        placeholder="email@persona-b.com"
                      />
                    </div>
                    <div>
                      <Label>Pasaporte</Label>
                      <Input
                        value={linkedPassport2}
                        onChange={(e) => setLinkedPassport2(e.target.value.toUpperCase())}
                        placeholder="ABC123456"
                      />
                    </div>
                    <div>
                      <Label>NIE</Label>
                      <Input
                        value={linkedNie2}
                        onChange={(e) => setLinkedNie2(e.target.value.toUpperCase())}
                        placeholder="X1234567Y"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Tipo de pago (Persona B)</Label>
                      <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="linkedPayment2"
                            checked={linkedPaymentType2 === 'one_time'}
                            onChange={() => setLinkedPaymentType2('one_time')}
                          />
                          <span>2 pagos (300€)</span>
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="linkedPayment2"
                            checked={linkedPaymentType2 === 'subscription'}
                            onChange={() => setLinkedPaymentType2('subscription')}
                          />
                          <span>10 cuotas (340€)</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Calificación y Coste */}
              <div>
                <h3 className="font-semibold text-lg mb-4 text-gray-900">
                  Calificación y Estudio Migro
                </h3>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800 mb-2">
                    <strong>Sistema de Calificación:</strong>
                  </p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li><strong>Nota A:</strong> Excelente - Alta probabilidad de éxito</li>
                    <li><strong>Nota B:</strong> Bueno - Probabilidad buena de éxito</li>
                    <li><strong>Nota C:</strong> Complejo - Requiere estudio adicional</li>
                    <li><strong>Nota T:</strong> Testing - Solo para pruebas internas (1€)</li>
                  </ul>
                  <p className="text-sm text-blue-800 mt-2 pt-2 border-t border-blue-200">
                    El importe depende del <strong>tipo de contrato</strong> elegido arriba (Individual 400€/480€, Unidad familiar o Pareja 600€/680€, 2x1 300€/340€), no de la Nota. La Nota es la calificación del expediente.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {(['A', 'B', 'C', 'T'] as ClientGrade[]).map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGrade(g)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        grade === g
                          ? (g === 'T' ? 'border-yellow-500 bg-yellow-500 text-white' : 'border-primary bg-primary text-white')
                          : 'border-gray-300 bg-white hover:border-primary'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-3xl font-bold mb-2">Nota {g}</div>
                        <div className="text-sm">{GRADE_DESCRIPTIONS[g]}</div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Selección de Tipo de Pago */}
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Tipo de Pago</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      type="button"
                      onClick={() => setPaymentType('one_time')}
                      className={`p-5 rounded-lg border-2 transition-all text-left ${
                        paymentType === 'one_time'
                          ? 'border-primary bg-primary/10 shadow-md'
                          : 'border-gray-300 bg-white hover:border-primary hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentType === 'one_time'
                            ? 'border-primary bg-primary'
                            : 'border-gray-400'
                        }`}>
                          {paymentType === 'one_time' && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 mb-2 text-lg">Pago en 2 plazos</h5>
                          <p className="text-sm text-gray-600 mb-1">50% al contratar + 50% tras comunicación favorable</p>
                          <p className="text-xs text-gray-500 mb-3">2 pagos en total</p>
                          <p className="text-xl font-bold text-primary">
                            {getEffectivePricing().one_time} EUR total
                          </p>
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaymentType('subscription')}
                      className={`p-5 rounded-lg border-2 transition-all text-left ${
                        paymentType === 'subscription'
                          ? 'border-primary bg-primary/10 shadow-md'
                          : 'border-gray-300 bg-white hover:border-primary hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentType === 'subscription'
                            ? 'border-primary bg-primary'
                            : 'border-gray-400'
                        }`}>
                          {paymentType === 'subscription' && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 mb-2 text-lg">Pago mensual</h5>
                          <p className="text-sm text-gray-600 mb-1">10 cuotas mensuales</p>
                          <p className="text-xs text-gray-500 mb-3">Pago automático cada mes</p>
                          <p className="text-xl font-bold text-primary">
                            {getEffectivePricing().subscription} EUR total
                          </p>
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaymentType('one_time_full')}
                      className={`p-5 rounded-lg border-2 transition-all text-left ${
                        paymentType === 'one_time_full'
                          ? 'border-primary bg-primary/10 shadow-md'
                          : 'border-gray-300 bg-white hover:border-primary hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentType === 'one_time_full'
                            ? 'border-primary bg-primary'
                            : 'border-gray-400'
                        }`}>
                          {paymentType === 'one_time_full' && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 mb-2 text-lg">Pago único (100%)</h5>
                          <p className="text-sm text-gray-600 mb-1">Un solo cargo por el importe total</p>
                          <p className="text-xs text-gray-500 mb-3">Precio personalizado (EUR)</p>
                          <p className="text-xl font-bold text-primary">
                            {paymentType === 'one_time_full' ? `${oneTimeFullAmount} EUR` : 'Indicar abajo'}
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Input importe para Pago único 100% */}
                  {paymentType === 'one_time_full' && (
                    <div className="mt-4 max-w-xs">
                      <label htmlFor="one_time_full_amount" className="block text-sm font-medium text-gray-700 mb-1">
                        Importe total (EUR) *
                      </label>
                      <input
                        id="one_time_full_amount"
                        type="number"
                        min={1}
                        step={1}
                        value={oneTimeFullAmount}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          setOneTimeFullAmount(Number.isNaN(v) ? 0 : Math.max(0, v));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder={String(getEffectivePricing().one_time)}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Precio estándar para este tipo de contrato: <strong>{getEffectivePricing().one_time}€</strong>. Modifica solo si acordaste un importe diferente con el cliente.
                      </p>
                    </div>
                  )}
                </div>

                {/* Resumen de Pago */}
                <div className="mt-4 bg-gray-50 border-2 border-gray-300 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Resumen de Pago</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Coste Total (IVA incluido):</span>
                      <span className="font-bold text-lg text-primary">{paymentInfo.total}€</span>
                    </div>
                    {paymentType === 'one_time_full' ? (
                      <>
                        <div className="flex justify-between border-t pt-2">
                          <span className="text-gray-600">Modalidad:</span>
                          <span className="font-semibold">Un solo pago</span>
                        </div>
                        <p className="text-xs text-gray-600 border-t pt-2 mt-2">
                          El cliente queda amparado por la garantía de devolución por dos resoluciones denegatorias (cláusula cuarta del contrato).
                        </p>
                      </>
                    ) : paymentType === 'subscription' ? (
                      <>
                        <div className="flex justify-between border-t pt-2">
                          <span className="text-gray-600">Pago mensual:</span>
                          <span className="font-semibold">{paymentInfo.monthly.toFixed(2)}€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Número de pagos:</span>
                          <span className="font-semibold">{paymentInfo.numberOfPayments} pagos mensuales</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between border-t pt-2">
                          <span className="text-gray-600">Primer pago (al contratar):</span>
                          <span className="font-semibold">{paymentInfo.first}€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Segundo pago (tras aprobación):</span>
                          <span className="font-semibold">{paymentInfo.second}€</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              </>)}

              {/* Anexos al Contrato */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-lg text-gray-900">
                    Anexos al Contrato (Opcional)
                  </h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddAnnex}
                    className="flex items-center gap-2"
                  >
                    <PlusIcon width={16} height={16} />
                    Agregar Anexo
                  </Button>
                </div>

                {annexes.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-gray-500 text-sm">
                    No hay anexos agregados. Puedes agregar anexos adicionales al contrato.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {annexes.map((annex, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                              <h4 className="font-semibold text-gray-900">{annex.title}</h4>
                            </div>
                            <p className="text-sm text-gray-600 whitespace-pre-wrap line-clamp-3">
                              {annex.content}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditAnnex(index)}
                              className="flex items-center gap-1"
                            >
                              <PencilIcon width={14} height={14} />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAnnex(index)}
                              className="flex items-center gap-1 text-red-600 hover:text-red-700"
                            >
                              <TrashIcon width={14} height={14} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pago Manual */}
              <div>
                <h3 className="font-semibold text-lg mb-4 text-gray-900">
                  Pago Manual (Opcional)
                </h3>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="manual-payment-admin"
                      checked={manualPaymentMode}
                      onCheckedChange={(checked) => {
                        const isChecked = checked === true;
                        setManualPaymentMode(isChecked);
                        if (!isChecked) {
                          setManualPaymentNote('');
                        }
                      }}
                    />
                    <div className="flex-1">
                      <Label htmlFor="manual-payment-admin" className="text-base font-semibold text-gray-900 cursor-pointer">
                        El cliente ya pagó por otro medio
                      </Label>
                      <p className="text-sm text-gray-700 mt-1">
                        Activa esta opción si el cliente abonó por transferencia, efectivo u otro método. 
                        El código generado omitirá el paso de Stripe y permitirá firmar directamente el contrato.
                      </p>
                    </div>
                  </div>

                  {manualPaymentMode && (
                    <div className="mt-4 space-y-2">
                      <Label htmlFor="manual-payment-note" className="text-sm font-semibold">
                        Describe cómo se realizó el pago *
                      </Label>
                      <Textarea
                        id="manual-payment-note"
                        value={manualPaymentNote}
                        onChange={(e) => setManualPaymentNote(e.target.value)}
                        placeholder="Ej: Pago recibido en efectivo el 19/11/2025"
                        rows={3}
                        required={manualPaymentMode}
                      />
                      <p className="text-xs text-gray-600">
                        Esta nota se incluirá en el contrato. El código generado tendrá status "paid" automáticamente y NO requerirá Stripe.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Error */}
              {error && (
                <div data-testid="contract-create-error" className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
                  <ExclamationCircleIcon width={20} height={20} />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(listPath)}
                  className="flex-1"
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="contract-create-submit"
                  disabled={loading}
                  className="flex-1 bg-primary hover:bg-primary-700 text-white"
                >
                  {loading ? 'Generando...' : 'Generar Código de Contratación'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Modal para crear/editar anexo */}
      {showAnnexModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {editingAnnexIndex !== null ? 'Editar Anexo' : 'Agregar Nuevo Anexo'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="annex-title">Título del Anexo *</Label>
                  <Input
                    id="annex-title"
                    value={annexForm.title}
                    onChange={(e) => setAnnexForm({ ...annexForm, title: e.target.value })}
                    placeholder="Ej: Anexo I - Condiciones Especiales"
                  />
                </div>
                
                <div>
                  <Label htmlFor="annex-content">Contenido del Anexo *</Label>
                  <Textarea
                    id="annex-content"
                    value={annexForm.content}
                    onChange={(e) => setAnnexForm({ ...annexForm, content: e.target.value })}
                    placeholder="Escribe el contenido del anexo aquí..."
                    rows={10}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Puedes escribir el contenido completo del anexo. Se guardará como texto plano.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAnnexModal(false);
                    setAnnexForm({ title: '', content: '' });
                    setEditingAnnexIndex(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveAnnex}
                  disabled={!annexForm.title.trim() || !annexForm.content.trim()}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {editingAnnexIndex !== null ? 'Guardar Cambios' : 'Agregar Anexo'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

